import { SettingsSectionWrapper1 } from "../../pageComponents/settings/style";
import {
  Box,
  Typography,
  Paper,
  Divider,
  CircularProgress,
  Chip,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { HeaderButton2 } from "../../pageComponents/style";
import { PaymentSagaActions } from "../../store/payment/sagas";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  isButtonLoadingSelector,
  isLoadingSelector,
  userCardDetailsSelector,
} from "../../store/payment/selector";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { currentPlanSelector } from "../../store/account/selector";
import { getRedirectUrl } from "../../utils/redirect";
import { useLocation, useNavigate } from "react-router-dom";
import { AccountSagaActions } from "../../store/account/sagas";
import { AccountReducerAction } from "../../store/account/slice";

const ChooseSavedCard = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const subscriptionId = location?.state?.subscriptionId || null;
  const isSeatPurchase = location?.state?.isSeatPurchase || null;
  const seatSummary = location?.state?.seatSummary || {};

  const navigate = useNavigate();
  const userCardDetails = useSelector(userCardDetailsSelector);
  const userDetails = JSON.parse(localStorage.getItem("User") as string);
  const currentPlan = useSelector(currentPlanSelector);
  const isLoading = useSelector(isLoadingSelector);
  const isButtonloading = useSelector(isButtonLoadingSelector);
  const [cardIdForloading, setCardIdForloading] = useState<string | null>(null);

  const handleNavigate = (url: string) => {
    navigate(getRedirectUrl(url), { replace: true });
    setCardIdForloading(null);
  };

  useEffect(() => {
    dispatch(PaymentSagaActions.GetUserCardDetails());
  }, [dispatch]);

  useEffect(() => {
    if (subscriptionId) {
      dispatch(AccountSagaActions.getCurrentPlan({ id: subscriptionId }));
    }

    return () => {
      dispatch(AccountReducerAction.setCurrentPlan({}));
    };
  }, [subscriptionId]);

  const handlePaymentWithToken = (cardId: string) => {
    setCardIdForloading(cardId);
    if (!isSeatPurchase && userDetails?._id && currentPlan?._id) {
      const data = {
        userId: userDetails?._id,
        planId: currentPlan?._id,
        cardId: cardId,
        amount: {
          value: Number(currentPlan?.amount) * 100,
          currency: "USD",
          countryCode: "USA",
        },
      };
      dispatch(
        PaymentSagaActions.handleCheckoutPaymentWithToken({
          data,
          handleNavigate,
        })
      );
    } else {
      const data = {
        seatCount: seatSummary?.seatCount ?? 0,
        cardId: cardId,
        amount: {
          value: Number(seatSummary?.totalAmount ?? 0) * 100,
          currency: "USD",
          countryCode: "USA",
        },
      };
      dispatch(
        PaymentSagaActions.handlePaymentSeatWithToken({
          data,
          handleNavigate,
        })
      );
    }
  };

  return isLoading ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "40vh",
        minWidth: "60vw",
      }}
    >
      <CircularProgress />
    </Box>
  ) : userCardDetails.length ? (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
        minWidth: "60vw",
        padding: "1rem",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 2,
          width: "100%",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {userCardDetails?.map((card: any, index: number) => (
          <Paper
            elevation={3}
            sx={{
              p: 3,
              borderRadius: 3,
              width: "100%",
              maxWidth: 400,
              background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
              color: "#fff",
              position: "relative",
              overflow: "hidden",
              boxShadow: card.isDefault
                ? "0px 0px 20px rgba(30, 60, 114, 0.6)"
                : "0px 2px 10px rgba(0, 0, 0, 0.1)",
              transition: "all 0.3s ease",
              "&:hover": {
                boxShadow: card.isDefault
                  ? "0px 0px 25px rgba(30, 60, 114, 0.8)"
                  : "0px 4px 12px rgba(0, 0, 0, 0.15)",
              },
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='240' viewBox='0 0 400 240'><path fill='%23ffffff11' d='M0,80 C50,140 150,20 200,80 C250,140 350,20 400,80 L400,240 L0,240 Z'/><path fill='%23ffffff22' d='M0,160 C50,220 150,100 200,160 C250,220 350,100 400,160 L400,240 L0,240 Z'/></svg>")`,
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
                opacity: 0.6,
                zIndex: 0,
              },
            }}
            key={index}
          >
            {/* Contents go here */}
            <Box position="relative" zIndex={1}>
              {/* Top Branding */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={4}
              >
                <Typography fontWeight={600} fontSize={20}>
                  Card Details
                </Typography>
                {card?.isDefault && (
                  <Chip
                    icon={
                      <CheckCircleIcon
                        sx={{
                          width: 20,
                          height: 20,
                          color: "#ffffff !important",
                        }}
                      />
                    }
                    label="Default Card"
                    sx={{
                      backgroundColor: "#ffffff22",
                      color: "#fff",
                      border: "1px solid #ffffff55",
                      fontWeight: 500,
                      height: 28,
                      fontSize: 14,
                    }}
                  />
                )}
              </Box>

              {/* Chip and Card Number */}
              <Box display="flex" alignItems="center" gap={2} mb={4}>
                <Box
                  sx={{
                    width: 40,
                    height: 30,
                    borderRadius: "6px",
                    background: "linear-gradient(45deg, #d4af37, #f5d76e)",
                  }}
                />
                <Typography fontSize={24} letterSpacing={2}>
                  **** **** **** {card.lastFourDigits}
                </Typography>
              </Box>

              {/* Card Holder & Expiry */}
              <Box display="flex" justifyContent="space-between">
                <Box>
                  <Typography fontSize={12} color="#ccc">
                    Card Holder
                  </Typography>
                  <Typography fontSize={16} fontWeight="bold">
                    {card.cardHolderName || "N/A"}
                  </Typography>
                </Box>

                <Box>
                  <Typography fontSize={12} color="#ccc">
                    Expiry Date
                  </Typography>
                  <Typography fontSize={16} fontWeight="bold">
                    {card.expiryDate}
                  </Typography>
                </Box>
              </Box>

              <HeaderButton2
                variant="contained"
                size="small"
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => handlePaymentWithToken(card?._id)}
                disabled={isButtonloading && cardIdForloading === card?._id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography fontWeight="bold" fontSize="0.875rem">
                  {isButtonloading && cardIdForloading === card?._id && (
                    <CircularProgress
                      size={18}
                      color="inherit"
                      sx={{ mr: 1 }}
                    />
                  )}
                  Proceed with this Card
                </Typography>
              </HeaderButton2>
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  ) : (
    <Box
      sx={{
        minHeight: "40vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Typography variant="h6" color="text.secondary">
        Looks like you haven’t added a payment method yet!
      </Typography>
    </Box>
  );
};

export default ChooseSavedCard;
